import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
	rectSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import classNames from 'classnames';

import { updatePageModule } from '../../../../../../store/actions/admin/create-event/session';
import {
	updateSpeaker,
} from '../../../../../../store/actions/admin/speakers';
import { EventsState } from '../../../../../../store/types';
import {
	ColorKeys,
	CreateSpeaker,
	LanguagesAbbr,
	PageModule,
	RelatedSpeakerSessions,
	Speaker,
	SpeakerPageModuleContent,
	TextOverrides,
} from '../../../../../../types/working-model';
import CreateSpeakerModal from '../../../../content-creation/speakers/speaker';
import SpeakerCard from './speaker';
import Controls from '../../session-module-controls/controls';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import { getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import { EContentColorOverrideNames } from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import LargeButton from '../../../../../general-ui/button/large-button';
import { ETabLayoutClassNames } from '../../../../../live-event/session/session';
import { ParamsProps } from '../../../../../live-event/live-event';
import { getStylingOverrides } from '../../../../../live-event/utils';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';

import '../../../../../../scss/live-event/base/session/speakers.scss';
import './speakers.scss';

interface Props {
	page_module: PageModule;
	template: string;
	tabLayout: ETabLayoutClassNames;
}

export default function Speakers(props: Props): JSX.Element | null {
	const { page_module, template, tabLayout } = props;
	const {
		CreateSessionReducer: {
			workingSession
		}
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);

	const [modalOpen, setModalOpen] = useState(false);
	const [workingSpeaker, setWorkingSpeaker] = useState<
		CreateSpeaker | Speaker | null
	>(null);
	const [replacing, setReplacing] = useState<Speaker | null>(null);
	const { language } = useParams() as ParamsProps;

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8
			}
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const content: SpeakerPageModuleContent = page_module.content;
	const stylingOverrides = page_module?.styling_overrides;
	const { backgroundColor, backgroundImg } = getStylingOverrides(stylingOverrides);

	function handleDragEnd(event: any) {
		if (!page_module.modules) return;
		const _modules = [...page_module.modules];

		const startIndex = _modules.findIndex(
			(speaker: Speaker) =>
				Number(speaker.speaker) === Number(event.active.id)
		);
		const newIndex = _modules.findIndex(
			(speaker: Speaker) =>
				Number(speaker.speaker) === Number(event.over.id)
		);

		const [module] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, module);

		dispatch(
			updatePageModule({
				...page_module,
				modules: _modules,
				content_modules: _modules.map(
					(speaker: Speaker) => speaker.speaker
				),
				is_edited: true,
			})
		);
	}

	function getRelatedSessionsComplete(relatedSessions: number[], speaker: number | undefined) {
		if (relatedSessions && speaker) {

			let relatedSessionExists = false;
			const _relatedSessions = page_module.content?.related_sessions?.map((r: RelatedSpeakerSessions) => {
				if (speaker === r.speaker) {
					relatedSessionExists = true;
					return {
						speaker,
						sessions: relatedSessions
					};
				}
				return r;
			}) ?? [];
			// if speaker doesnt already have related_sessions
			if (!relatedSessionExists) {
				_relatedSessions?.push({
					speaker,
					sessions: relatedSessions
				});
			}
			return _relatedSessions;
		}
		return null;
	}

	function editSpeaker(speaker: Speaker) {
		return () => {
			setWorkingSpeaker(speaker);
			setModalOpen(true);
		};
	}

	function replaceSpeaker(speaker: Speaker) {
		return () => {
			setReplacing(speaker);
			setModalOpen(true);
		};
	}

	function checkDeleteSpeaker(speaker: Speaker) {
		return () => {
			if (page_module.modules) {

				const _modules = page_module?.modules.slice();
				const filteredSpeakers = _modules.filter((s: Speaker) => s.speaker !== speaker.speaker);

				dispatch(updatePageModule({
					...page_module,
					content_modules: filteredSpeakers.map((s: Speaker) => s.speaker),
					modules: filteredSpeakers,
					is_edited: true,
				}));
			}
		};
	}

	function closeModal() {
		setModalOpen(false);
		// prevent flashing state
		setTimeout(() => {
			setWorkingSpeaker(null);
			setReplacing(null);
		}, 400);
	}

	function onSelectionComplete(speakerList: Speaker[]) {
		dispatch(
			updatePageModule({
				...page_module,
				content_modules: speakerList.map(s => s.speaker),
				modules: [...speakerList],
				is_edited: true,
			})
		);
	}
	function onSpeakerComplete(speaker: Speaker, relatedSessions: number[]) {
		const updatedModules = page_module.modules?.map((module: Speaker) => {
			if (module.speaker === (speaker as Speaker).speaker) {
				return speaker;
			}
			return module;
		});

		const _relatedSessions = getRelatedSessionsComplete(relatedSessions, speaker.speaker);

		if ((workingSpeaker as Speaker)?.speaker) {
			dispatch(updateSpeaker((speaker as Speaker)));
			dispatch(
				updatePageModule({
					...page_module,
					modules: (updatedModules as Speaker[]),
					content: { ...page_module.content, related_sessions: _relatedSessions },
					is_edited: true,
				})
			);
		} else {
			dispatch(
				updatePageModule({
					...page_module,
					content_modules: [
						...(page_module?.content_modules ?? []),
						speaker.speaker,
					],
					modules: [...(page_module?.modules ?? []), speaker],
					content: { ...page_module.content, related_sessions: _relatedSessions },
					is_edited: true,
				})
			);
		}
		setModalOpen(false);
	}

	function handleTextChange(key: string) {
		return (text: string) => {
			if (!workingSession) return;
			dispatch(
				updatePageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							baseLanguage: workingSession.default_language,
							language
						})
					},
					is_edited: true,
				})
			);
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...stylingOverrides,
				content: {
					...stylingOverrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updatePageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}
	const { custom_heading } = getTextValue(content, language);

	return page_module.is_on && (!workingSession?.layout?.withMenu || page_module) ? (
		<div
			className={classNames(
				"editor-wrapper speakers",
				`speakers-${tabLayout}-tabs`,
				template,
				backgroundColor
			)}
			style={backgroundImg}
		>
			<Controls
				page_module={page_module}
				openEditModal={() => setModalOpen(true)}
			/>
			<div className="container" >
				<EditableTextSlim
					tagType="h5"
					className="speakers-title"
					value={t(`${namespace}:speakers.${page_module.id}.heading`, t(`${namespace}:speakers.heading`, custom_heading))}
					defaultColor={ColorKeys.bodyTextColor}
					onChange={handleTextChange('custom_heading')}
					onOverrideChange={handleStyleOverrideChange('custom_heading')}
					elementName={EContentColorOverrideNames.custom_heading}
					colorOverride={stylingOverrides?.content?.custom_heading?.color}
				/>
				{page_module.modules && page_module.modules.length ? (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
						modifiers={[restrictToParentElement]}
						autoScroll={false}
					>
						<SortableContext
							items={page_module.modules.map(
								(module: Speaker) => String(module.speaker)
							)}
							strategy={rectSortingStrategy}
						>
							<div className="speakers-list">
								{page_module.modules.map(
									(speaker: Speaker, index: number) => (
										<SpeakerCard
											speaker={speaker}
											key={index}
											id={String(speaker.speaker)}
											editSpeaker={editSpeaker}
											checkDeleteSpeaker={checkDeleteSpeaker}
											replaceSpeaker={replaceSpeaker}
											template={template}
										/>
									)
								)}
							</div>
						</SortableContext>
					</DndContext>
				) : (
					<LargeButton
						title="Add Speakers"
						onClick={() => setModalOpen(true)}
						nonUpload
					/>
				)

				}
			</div>
			<CreateSpeakerModal
				language={language}
				baseLanguage={workingSession?.default_language as LanguagesAbbr}
				open={modalOpen}
				close={closeModal}
				editingSpeaker={workingSpeaker}
				onComplete={onSpeakerComplete}
				onSelectionComplete={onSelectionComplete}
				initialSelected={page_module.modules}
				replacing={replacing}
				initialRelatedSessions={page_module.content?.related_sessions}
			/>
		</div>
	) : null;
}
